import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  CircularProgress,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getInventoryItemsByVariant } from '../../inventory/services/inventoryItemService';

// Status configurations
const STATUS_CONFIG = {
  1: { label: 'Available', color: 'success' },
  2: { label: 'Sold', color: 'error' },
  3: { label: 'Reserved', color: 'warning' },
  4: { label: 'Returned', color: 'info' },
  5: { label: 'Damaged', color: 'error' },
  6: { label: 'In Transit', color: 'secondary' },
};

const EMPTY_ROW = () => ({ imei: '', serialNumber: '', notes: '', warrantyExpiryDate: '' });

/**
 * IMEISerialDialog - Reusable component for managing IMEI/Serial numbers
 * 
 * Features:
 * - Two tabs: Add New and Existing Inventory
 * - Entry mode selection (IMEI or Serial Number)
 * - Search and filter existing inventory items
 * - Bulk selection of existing items
 * 
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close handler
 * @param {number} quantity - Expected quantity
 * @param {string} variantName - Display name for variant/product
 * @param {number} variantId - Variant ID for fetching existing items
 * @param {number} productId - Product ID (fallback if no variant)
 * @param {array} existingItems - Pre-filled items
 * @param {function} onSave - Save handler with items array
 */
const IMEISerialDialog = ({
  open,
  onClose,
  quantity = 1,
  variantName = '',
  variantId = null,
  productId = null,
  existingItems = [],
  onSave,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [entryMode, setEntryMode] = useState('imei');
  
  // Add New tab state
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  
  // Existing Inventory tab state
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedInventoryItems, setSelectedInventoryItems] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState('1'); // Default to Available
  const [searchTerm, setSearchTerm] = useState('');
  
  // Initialize rows when dialog opens
  useEffect(() => {
    if (!open) return;
    
    const count = Math.max(quantity, existingItems.length, 1);
    const initialRows = Array.from({ length: count }, (_, i) => ({
      imei: existingItems[i]?.imei || '',
      serialNumber: existingItems[i]?.serialNumber || '',
      notes: existingItems[i]?.notes || '',
      warrantyExpiryDate: existingItems[i]?.warrantyExpiryDate || '',
    }));
    setRows(initialRows);
    setErrors({});
    setSelectedInventoryItems([]);
  }, [open, quantity, existingItems]);
  
  // Load existing inventory when tab switches
  useEffect(() => {
    if (open && activeTab === 1 && variantId) {
      loadExistingInventory();
    }
  }, [open, activeTab, variantId, page, rowsPerPage, statusFilter, searchTerm]);
  
  const loadExistingInventory = async () => {
    setLoadingInventory(true);
    try {
      const response = await getInventoryItemsByVariant(variantId, {
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        statuses: [parseInt(statusFilter)],
        searchTerm: searchTerm,
      });
      
      console.log('API Response:', response);
      
      // Handle different response structures
      let items = [];
      let total = 0;
      
      // Check for payload.Items structure (your API format)
      if (response?.payload?.Items) {
        items = response.payload.Items;
        total = response.payload.TotalCount || response.payload.TotalItems || items.length;
      } else if (response?.data?.payload?.Items) {
        items = response.data.payload.Items;
        total = response.data.payload.TotalCount || response.data.payload.TotalItems || items.length;
      } else if (response?.data?.payload) {
        items = Array.isArray(response.data.payload) ? response.data.payload : [];
        total = response.data.totalCount || response.data.TotalCount || items.length;
      } else if (response?.payload) {
        items = Array.isArray(response.payload) ? response.payload : [];
        total = response.totalCount || response.TotalCount || items.length;
      } else if (response?.data) {
        items = Array.isArray(response.data) ? response.data : [];
        total = items.length;
      } else if (Array.isArray(response)) {
        items = response;
        total = items.length;
      }
      
      console.log('Processed items:', items, 'Total:', total);
      setInventoryItems(items);
      setTotalItems(total);
    } catch (error) {
      console.error('Error loading inventory:', error);
      toast.error('Failed to load existing inventory items');
      setInventoryItems([]);
      setTotalItems(0);
    } finally {
      setLoadingInventory(false);
    }
  };
  
  const handleChange = (idx, field, value) => {
    const updatedRows = rows.map((row, i) => 
      i === idx ? { ...row, [field]: value } : row
    );
    setRows(updatedRows);
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${idx}_${field}`];
      return next;
    });
  };
  
  const handleAddRow = () => {
    setRows((prev) => [...prev, EMPTY_ROW()]);
  };
  
  const handleRemoveRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };
  
  const validate = () => {
    const errs = {};
    const imeiSet = new Set();
    const serialSet = new Set();
    
    rows.forEach((row, i) => {
      const imeiVal = row.imei.trim();
      const serialVal = row.serialNumber.trim();
      
      if (entryMode === 'imei') {
        if (!imeiVal) {
          errs[`${i}_imei`] = 'IMEI is required';
        } else if (imeiSet.has(imeiVal)) {
          errs[`${i}_imei`] = 'Duplicate IMEI';
        } else {
          imeiSet.add(imeiVal);
        }
      } else {
        if (!serialVal) {
          errs[`${i}_serialNumber`] = 'Serial Number is required';
        } else if (serialSet.has(serialVal)) {
          errs[`${i}_serialNumber`] = 'Duplicate Serial Number';
        } else {
          serialSet.add(serialVal);
        }
      }
    });
    return errs;
  };
  
  const filledCount = entryMode === 'imei' 
    ? rows.filter((r) => r.imei.trim()).length 
    : rows.filter((r) => r.serialNumber.trim()).length;
  
  const isComplete = filledCount === rows.length && rows.length > 0;
  
  const handleSave = () => {
    if (activeTab === 0) {
      // Add New tab
      const errs = validate();
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        return;
      }
      if (onSave) {
        onSave(rows.map((r) => ({ 
          ...r, 
          imei: r.imei.trim(), 
          serialNumber: r.serialNumber.trim() 
        })));
      }
    } else {
      // Existing Inventory tab
      if (selectedInventoryItems.length === 0) {
        toast.warning('Please select at least one inventory item');
        return;
      }
      if (onSave) {
        const selectedItems = selectedInventoryItems.map(item => ({
          imei: item.IMEI || item.imei || '',
          serialNumber: item.SerialNumber || item.serialNumber || '',
          notes: item.Notes || item.notes || '',
          warrantyExpiryDate: item.WarrantyExpiryDate || item.warrantyExpiryDate || '',
          inventoryItemId: item.Id || item.id,
        }));
        onSave(selectedItems);
      }
    }
    onClose();
  };
  
  const handleInventoryItemToggle = (item) => {
    setSelectedInventoryItems(prev => {
      const exists = prev.find(i => i.Id === item.Id);
      if (exists) {
        return prev.filter(i => i.Id !== item.Id);
      } else {
        return [...prev, item];
      }
    });
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleDialogClose} 
      maxWidth="lg" 
      fullWidth 
      PaperProps={{ sx: { borderRadius: 2, minHeight: '600px' } }} 
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Manage IMEI / Serial Numbers</Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{variantName}</strong> — quantity: <strong>{quantity}</strong>
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {activeTab === 1 && variantId && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                label="Status"
              >
                {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                  <MenuItem key={value} value={value}>
                    <Chip label={config.label} color={config.color} size="small" />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {activeTab === 0 && (
            <Chip
              label={`${filledCount} entered`}
              color={isComplete ? 'success' : filledCount > 0 ? 'warning' : 'default'}
              size="small"
              icon={isComplete ? <CheckCircleIcon /> : undefined}
            />
          )}
          {activeTab === 1 && (
            <Chip
              label={`${selectedInventoryItems.length} selected`}
              color={selectedInventoryItems.length > 0 ? 'success' : 'default'}
              size="small"
            />
          )}
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Add New" />
        <Tab label="Existing Inventory" disabled={!variantId} />
      </Tabs>
      
      <DialogContent dividers sx={{ p: 2 }}>
        {/* Tab 1: Add New */}
        {activeTab === 0 && (
          <Box>
            {/* Entry Mode Selection */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <RadioGroup
                row
                value={entryMode}
                onChange={(e) => setEntryMode(e.target.value)}
              >
                <FormControlLabel value="imei" control={<Radio />} label="IMEI" />
                <FormControlLabel value="serial" control={<Radio />} label="Serial Number" />
              </RadioGroup>
            </Box>
            
            {rows.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No rows. Click "Add" below to add entries.</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell width={40}>#</TableCell>
                    <TableCell><strong>{entryMode === 'imei' ? 'IMEI *' : 'Serial Number *'}</strong></TableCell>
                    <TableCell><strong>Warranty Expiry</strong></TableCell>
                    <TableCell><strong>Notes</strong></TableCell>
                    <TableCell width={100} align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder={entryMode === 'imei' ? '123456789012345' : 'SN001'}
                          value={entryMode === 'imei' ? row.imei : row.serialNumber}
                          onChange={(e) => handleChange(i, entryMode === 'imei' ? 'imei' : 'serialNumber', e.target.value)}
                          error={Boolean(errors[`${i}_${entryMode === 'imei' ? 'imei' : 'serialNumber'}`])}
                          helperText={errors[`${i}_${entryMode === 'imei' ? 'imei' : 'serialNumber'}`]}
                        />
                      </TableCell>
                      
                      <TableCell>
                        <TextField
                          size="small"
                          type="date"
                          fullWidth
                          value={row.warrantyExpiryDate}
                          onChange={(e) => handleChange(i, 'warrantyExpiryDate', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g. New"
                          value={row.notes}
                          onChange={(e) => handleChange(i, 'notes', e.target.value)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {i === rows.length - 1 && (
                            <IconButton size="small" color="primary" onClick={handleAddRow}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          )}
                          {rows.length > 1 && (
                            <IconButton size="small" color="error" onClick={() => handleRemoveRow(i)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
        
        {/* Tab 2: Existing Inventory */}
        {activeTab === 1 && (
          <Box>
            {/* Search Filter */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search IMEI or Serial Number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
            </Stack>
            
            {loadingInventory ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : inventoryItems.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No inventory items found</Typography>
              </Box>
            ) : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell width={40}></TableCell>
                      <TableCell><strong>IMEI</strong></TableCell>
                      <TableCell><strong>Serial Number</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Notes</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventoryItems.map((item) => {
                      const isSelected = selectedInventoryItems.some(i => i.Id === item.Id);
                      return (
                        <TableRow 
                          key={item.Id} 
                          hover 
                          selected={isSelected}
                          onClick={() => handleInventoryItemToggle(item)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            {isSelected && <CheckCircleIcon color="success" fontSize="small" />}
                          </TableCell>
                          <TableCell>{item.IMEI || item.imei || '-'}</TableCell>
                          <TableCell>{item.SerialNumber || item.serialNumber || '-'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={STATUS_CONFIG[item.Status]?.label || 'Unknown'} 
                              color={STATUS_CONFIG[item.Status]?.color || 'default'} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>{item.Notes || item.notes || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                
                <TablePagination
                  component="div"
                  count={totalItems}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          {activeTab === 0 
            ? `${rows.length - filledCount} row(s) need ${entryMode === 'imei' ? 'an IMEI' : 'a Serial Number'}`
            : `${selectedInventoryItems.length} item(s) selected`
          }
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={activeTab === 0 ? filledCount === 0 : selectedInventoryItems.length === 0}
          >
            Save
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default IMEISerialDialog;
